import React from 'react'
import cn from 'classnames'
import styles from './reply-button.module.scss'
import ReplyIcon from './icons/ReplyIcon'
import ReplyQuoteIcon from './icons/ReplyQuoteIcon'
import ReliableButton from './ReliableButton'
import type { Answer } from 'types/answers'

interface ReplyButtonProps {
    onClick: (event: React.MouseEvent) => void
    size?: number
    iconSize?: number
    className?: string
    answer?: Answer
    highlight?: boolean
    childCount?: number
    // The composer below is quoting this answer — see .quoteActive.
    quoteActive?: boolean
    // A press on the button leaves focus where it is: the reply lands in a
    // composer that is already on screen. On touch (ReliableButton) the
    // keyboard the reader is typing on stays up around it; with a mouse the
    // composer is not blurred, which would fold the bottom editor before the
    // click arrives.
    preserveFocus?: boolean
    // Compact ordinals-chat verb button (the Answer UI's thin controls).
    thin?: boolean
}

// A mouse press focuses the button, blurring whatever had focus. Keyboard
// and assistive-technology clicks (detail 0) fire no mousedown.
function preventMouseFocus(event: React.MouseEvent<HTMLButtonElement>) {
    if (event.button === 0) event.preventDefault()
}

export default function ReplyButton({
    onClick,
    size = 30,
    iconSize = 13,
    className,
    answer,
    highlight = false,
    childCount,
    quoteActive = false,
    preserveFocus = false,
    thin = false,
}: ReplyButtonProps) {
    // Prefer reactive childCount derived from the child quest so this re-renders
    // when more data flows in (via react-query cache updates or channel events).
    // The thin verb has no border to frame it, so its glyph and count read larger.
    const glyphSize = thin ? 19 : iconSize
    const countSize = thin ? 16 : iconSize
    const countLabel = childCount > 99 ? '99+' : String(childCount)
    const marginLeft = countLabel.length === 2 ? 2 : 4
    return (
        <div className={cn(styles.replyWrapper, className)}>
            <ReliableButton
                data-testid="answer-reply-button"
                className={cn(
                    styles.replyButton,
                    childCount > 0 && highlight && styles.hasReplies,
                    quoteActive && styles.quoteActive,
                    thin && styles.thin,
                )}
                onClick={onClick}
                onMouseDown={preserveFocus ? preventMouseFocus : undefined}
                preserveFocusOnTouch={preserveFocus}
                title="Reply"
                style={
                    thin
                        ? undefined
                        : { height: size, padding: `0 ${Math.max(6, Math.floor(iconSize / 2))}px` }
                }
                aria-label={`Reply${childCount > 0 ? ` (${countLabel})` : ''}`}
            >
                {thin ? (
                    <ReplyQuoteIcon size={glyphSize} strokeWidth={2} aria-hidden />
                ) : (
                    <ReplyIcon size={glyphSize} contextStyles={styles.replyIcon} className="" />
                )}
                {childCount > 0 && (
                    <span
                        className={styles.replyCountText}
                        style={{
                            fontSize: countSize,
                            lineHeight: `${countSize}px`,
                            height: countSize,
                            marginLeft,
                        }}
                    >
                        {countLabel}
                    </span>
                )}
            </ReliableButton>
        </div>
    )
}
